import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MarkdownEditor } from "@/components/common/markdown-editor";
import { createDashboardPost, updateDashboardPost } from "@/lib/api/dashboard-posts";

const TITLE_MAX_LENGTH = 200;
const BODY_MAX_LENGTH = 8000;

/**
 * Create/edit dialog for a dashboard announcement: plain title + markdown body (the same
 * MarkdownEditor as the staff bio). `post` null means create. Calls onSaved after every
 * server-visible outcome — including 403/404 failures — so the feed refetches either way.
 */
export function PostEditorDialog({ open, onOpenChange, post, onSaved }) {
  const isEdit = Boolean(post?.id);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-seed on every open so a reopened dialog never shows a previous draft.
  useEffect(() => {
    if (!open) return;
    setTitle(String(post?.title || ""));
    setBody(String(post?.body || ""));
    setSaving(false);
  }, [open, post]);

  const canSave = Boolean(title.trim()) && Boolean(body.trim()) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (isEdit) {
        await updateDashboardPost(post.id, { title, body });
        toast.success("Announcement updated");
      } else {
        await createDashboardPost({ title, body });
        toast.success("Announcement posted");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      // 403 = rights revoked mid-session, 404 = deleted under us; the refetch via onSaved
      // realigns the feed (and hides the editor affordances on the next canEdit).
      toast.error(error?.message || "Could not save the announcement");
      if (error?.status === 403 || error?.status === 404) {
        onOpenChange(false);
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Changes are visible to all staff immediately; the post keeps its original author."
              : "Visible to all staff on their Dashboard, with a red badge until they read it — and pushed to their phones."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={title}
            maxLength={TITLE_MAX_LENGTH}
            placeholder="Title"
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
          />
          <MarkdownEditor
            value={body}
            onChange={setBody}
            maxLength={BODY_MAX_LENGTH}
            rows={10}
            disabled={saving}
            placeholder="Write the announcement… markdown is supported."
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Post announcement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
